import { notifications } from "@mantine/notifications"
import { AlertCircle, CheckCircle } from "lucide-react"
import { createElement } from "react"
import { create } from "zustand"

import { cloudAllocationService } from "@/services/cloud-allocation.service"
import type {
  AllocatedResource,
  CloudAllocationCreateRequest,
  CloudIntegration,
  CloudIntegrationCreateRequest,
  CloudResource,
} from "@/types"

interface CloudAllocationState {
  integrations: CloudIntegration[]
  resources: CloudResource[]
  allocatedResources: AllocatedResource[]

  isLoadingIntegrations: boolean
  isLoadingResources: boolean
  isLoadingAllocations: boolean
  isSyncing: boolean
  isAllocating: boolean
  error: string | null

  fetchIntegrations: () => Promise<void>
  fetchResources: () => Promise<void>
  fetchAllocations: () => Promise<void>
  createIntegration: (data: CloudIntegrationCreateRequest) => Promise<CloudIntegration | null>
  syncResources: (integrationId: number) => Promise<boolean>
  createAllocation: (data: CloudAllocationCreateRequest) => Promise<boolean>
  removeAllocation: (allocationId: number) => Promise<boolean>
  clearError: () => void
}

export const useCloudAllocationStore = create<CloudAllocationState>((set) => ({
  integrations: [],
  resources: [],
  allocatedResources: [],
  isLoadingIntegrations: false,
  isLoadingResources: false,
  isLoadingAllocations: false,
  isSyncing: false,
  isAllocating: false,
  error: null,

  fetchIntegrations: async () => {
    set({ isLoadingIntegrations: true, error: null })
    try {
      const integrations = await cloudAllocationService.listIntegrations()
      set({ integrations, isLoadingIntegrations: false })
    } catch (error: any) {
      set({ error: error.message, isLoadingIntegrations: false })
    }
  },

  fetchResources: async () => {
    set({ isLoadingResources: true, error: null })
    try {
      const resources = await cloudAllocationService.listResources()
      set({ resources, isLoadingResources: false })
    } catch (error: any) {
      set({ error: error.message, isLoadingResources: false })
    }
  },

  fetchAllocations: async () => {
    set({ isLoadingAllocations: true, error: null })
    try {
      const allocatedResources = await cloudAllocationService.listAllocations()
      set({ allocatedResources, isLoadingAllocations: false })
    } catch (error: any) {
      set({ error: error.message, isLoadingAllocations: false })
    }
  },

  createIntegration: async (data: CloudIntegrationCreateRequest) => {
    set({ isLoadingIntegrations: true, error: null })
    try {
      const integration = await cloudAllocationService.createIntegration(data)
      set((state) => ({
        integrations: [...state.integrations, integration],
        isLoadingIntegrations: false,
      }))
      notifications.show({
        title: "Integration Created",
        message: `${integration.name} has been connected successfully`,
        color: "green",
        icon: createElement(CheckCircle, { size: 18 }),
        autoClose: 4000,
      })
      return integration
    } catch (error: any) {
      set({ error: error.message, isLoadingIntegrations: false })
      notifications.show({
        title: "Integration Failed",
        message: error.message,
        color: "red",
        icon: createElement(AlertCircle, { size: 18 }),
        autoClose: 5000,
      })
      return null
    }
  },

  syncResources: async (integrationId: number) => {
    set({ isSyncing: true, error: null })
    try {
      const result = await cloudAllocationService.syncResources(integrationId)
      const resources = await cloudAllocationService.listResources()
      set({ resources, isSyncing: false })
      notifications.show({
        title: "Sync Complete",
        message: `${result.synced_resources} resource(s) discovered`,
        color: "green",
        icon: createElement(CheckCircle, { size: 18 }),
        autoClose: 4000,
      })
      return true
    } catch (error: any) {
      set({ error: error.message, isSyncing: false })
      notifications.show({
        title: "Sync Failed",
        message: error.message,
        color: "red",
        icon: createElement(AlertCircle, { size: 18 }),
        autoClose: 5000,
      })
      return false
    }
  },

  createAllocation: async (data: CloudAllocationCreateRequest) => {
    set({ isAllocating: true, error: null })
    try {
      await cloudAllocationService.createAllocation(data)
      // Re-fetch to get the updated grouped list with the new allocation
      const allocatedResources = await cloudAllocationService.listAllocations()
      set({ allocatedResources, isAllocating: false })
      return true
    } catch (error: any) {
      set({ error: error.message, isAllocating: false })
      return false
    }
  },

  removeAllocation: async (allocationId: number) => {
    try {
      await cloudAllocationService.removeAllocation(allocationId)
      set((state) => ({
        allocatedResources: state.allocatedResources.filter((r) => r.allocation_id !== allocationId),
      }))
      notifications.show({
        title: "Server Removed",
        message: "Server has been unassigned from this department",
        color: "blue",
        icon: createElement(CheckCircle, { size: 18 }),
        autoClose: 4000,
      })
      return true
    } catch (error: any) {
      notifications.show({
        title: "Removal Failed",
        message: error.message,
        color: "red",
        icon: createElement(AlertCircle, { size: 18 }),
        autoClose: 5000,
      })
      return false
    }
  },

  clearError: () => set({ error: null }),
}))
